import { Component, ViewChild } from '@angular/core';
import { CodeEditorComponent, createProviders } from '../code-editor.component';
import { funcBodyDiagnosticCodesToIgnore } from '../../typescript/constants';
import { CopilotAnalysisComponent } from '../copilot-analysis/copilot-analysis.component';
import { IAnalysisFinding } from '../../services/copilot.service';

export interface ICodeHighlight {
  startLine: number;
  endLine?: number;
}

@Component({
    selector: 'app-code-input',
    templateUrl: './code-input.component.html',
    styleUrls: ['./code-input.component.scss'],
    providers: createProviders(CodeInputComponent),
    standalone: false
})
export class CodeInputComponent extends CodeEditorComponent {

  @ViewChild('analysis')
  analysis: CopilotAnalysisComponent;

  protected decorations: monaco.editor.IEditorDecorationsCollection = undefined;

  createEditorOptions(): monaco.editor.IStandaloneEditorConstructionOptions {
    return {
      model: monaco.editor.createModel('', 'fakeTypescript', monaco.Uri.parse(`file:///main_${this.id}.ts`)),
      language: 'fakeTypescript',
      automaticLayout: true,
      theme: 'vs'
    };
  }

  override onDidFocusEditor() {
    // see https://github.com/Microsoft/TypeScript/blob/master/src/compiler/diagnosticMessages.json for error messages
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      diagnosticCodesToIgnore: funcBodyDiagnosticCodesToIgnore
    });
  }

  public async analyzeCode() {
    await this.analysis.analyzeCode(this.value);
  }

  protected onFindingSelected(finding: IAnalysisFinding) {
    this.highlightCode("datex-copilot-content", finding.suggestion,
      this.toCodeHighlights(finding));
  }

  protected onClearSelected() {
    this.clearCodeHighlighting();
  }

  protected toCodeHighlights(finding: IAnalysisFinding): ICodeHighlight[] {
    var parts: string[] = [finding.lineNumber];
    const highlights: ICodeHighlight[] = [];

    if (finding.lineNumber.includes(",")) {
      parts = finding.lineNumber.split(",");
    } else if (finding.lineNumber.includes("&")) {
      parts = finding.lineNumber.split("&");
    }

    parts.forEach(part => {
      var startLine: number;
      var endLine: number = undefined;

      if (part.includes("-")) {
        const range = part.split("-");
        startLine = parseInt(range[0]);
        endLine = parseInt(range[1]);
      } else {
        startLine = parseInt(part);
      }

      highlights.push(<ICodeHighlight>({ startLine, endLine }));
    });

    return highlights;
  }

  protected highlightCode(className: string, suggestion: string, highlights: ICodeHighlight[]) {
    const decorations: monaco.editor.IModelDeltaDecoration[] = [];

    highlights.forEach(highlight => {
      decorations.push({
        range: new monaco.Range(highlight.startLine, CopilotAnalysisComponent.DEFAULT_START_COLUMN, highlight.endLine ?
          highlight.endLine : highlight.startLine, CopilotAnalysisComponent.DEFAULT_END_COLUMN),
        options: {
          isWholeLine: true,
          className: className,
          hoverMessage: { value: suggestion }
        },
      });
    });

    if (this.decorations) {
      this.decorations.set(decorations);
    } else {
      this.decorations = this.editor.createDecorationsCollection(decorations);
    }

    if (highlights.length > 0) {
      if (highlights[0].endLine) {
        this.editor.revealLinesNearTop(highlights[0].startLine, highlights[0].endLine, monaco.editor.ScrollType.Smooth);
      } else {
        this.editor.revealLineNearTop(highlights[0].startLine, monaco.editor.ScrollType.Smooth);
      }
    }
  }

  protected clearCodeHighlighting() {
    if (this.decorations) {
      this.decorations.clear();
    }
  }
}
